<script lang="ts" setup>
import type { Location } from "@mmh/types"
import { useDebounce } from "@vueuse/core"
import { AnimatePresence, Motion } from "motion-v"
import { PopoverArrow } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { computed, nextTick, ref } from "vue"
import { RouteLocationNamedRaw, useRoute, useRouter } from "vue-router"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { useMedia } from "~/composables/media"
import { useScrolled } from "~/composables/scroll"
import { titleCase } from "~/lib/case"
import { cn } from "~/lib/utils"
import { NAV_DEBOUNCE_MS } from "."

const { sidebar } = defineProps<{
    sidebar?: boolean
    class?: HTMLAttributes["class"]
}>()

const emits = defineEmits<{
    (e: "select"): void
}>()

const router = useRouter()
const route = useRoute()
const scrolled = useScrolled()
const { concept } = useConcept()
const { hasOneLocation, location } = useLocation()
const { isDesktop } = useMedia()

const isPopoverEnabled = computed(() => !hasOneLocation.value && isDesktop.value)

const oneLocationRouteSlug = computed(() => {
    const legalPages = ["privacy-policy", "terms-of-use", "accessibility-statement"]
    return hasOneLocation.value && legalPages.includes(route.name as string)
})

const locationItems = computed(() => {
    const items = (concept.value?.locations || []).map((location: Location): { name: string; to: RouteLocationNamedRaw } => {
        return {
            name: `${titleCase(location.name)}`,
            to: {
                name: "home",
                params: { name: location.path }
            }
        }
    })

    if (router.hasRoute("concept")) {
        items.push({
            name: "All Locations",
            to: { name: "concept" }
        })
    }
    
    return items
})

const to = computed(() => {
    if (hasOneLocation.value) {
        return { name: "home" }
    }
    if (!route.params.name) {
        return { name: route.name }
    }
    return {
        name: "home",
        params: { name: route.params.name }
    }
})

function handleOpen(value: boolean) {
    if (value && hasOneLocation.value) {
        open.value = false
    }
}

function handleOpenUpdate(value: boolean) {
    open.value = value
    handleOpen(value)
}

function shouldToggleMenu() {
    return !sidebar && !hasOneLocation.value
}

function toggleMenu(event: MouseEvent | KeyboardEvent) {
    event.preventDefault()
    event.stopPropagation()
    open.value = !open.value
}

function handleTriggerClick(event: MouseEvent) {
    if (sidebar) {
        emits("select")
        return
    }

    if (!shouldToggleMenu()) {
        return
    }

    toggleMenu(event)
}

function handleTriggerKeydown(event: KeyboardEvent) {
    if (!shouldToggleMenu()) {
        return
    }

    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        toggleMenu(event)
    }
}

function handleKeydown(e: KeyboardEvent, index: number) {
    const isFirst = index === 0
    const isLast = index === locationItems.value.length - 1

    if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        open.value = false
        nextTick(() => {
            triggerRef.value?.$el?.focus()
        })
    } else if ((e.key === "Tab" && !e.shiftKey && isLast) || (e.key === "Tab" && e.shiftKey && isFirst)) {
        e.preventDefault()
        e.stopPropagation()
        triggerRef.value?.$el?.focus()
        open.value = false
    } else if (e.key === "Enter") {
        e.preventDefault()
        router.push(locationItems.value[index].to)
        open.value = false
    }
}

function handleEscapeKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") {
        e.preventDefault()
        open.value = false
        nextTick(() => {
            triggerRef.value?.$el?.focus()
        })
    }
}

function handleSelect(to: RouteLocationNamedRaw) {
    open.value = false
    router.push(to)
    emits("select")
}

function isActive(to: RouteLocationNamedRaw) {
    return to.params?.name === route.params.name || (!to.params?.name && !route.params.name)
}

const open = ref(false)
const debouncedOpen = useDebounce(open, NAV_DEBOUNCE_MS)
const triggerRef = ref<{ $el: HTMLElement } | null>(null)

const logoVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } }
const logoTransition = { ease: "easeInOut" as const }

const ariaLabel = computed(() => {
    if (hasOneLocation.value) {
        return `Navigate to ${titleCase(concept.value?.name || "")} home`
    } else if (!isDesktop.value) {
        return `Navigate to ${titleCase(location.value?.name || "")} home`
    }
    return `Select location for ${titleCase(concept.value?.name || "")}`
})
</script>

<template>
    <Popover
        :open="debouncedOpen"
        @update:open="handleOpenUpdate"
    >
        <PopoverTrigger
            as-child
            @mouseenter="open = true"
            @mouseleave="open = false"
        >
            <RouterLink
                ref="triggerRef"
                :to="to"
                :role="isPopoverEnabled ? 'button' : undefined"
                :aria-haspopup="isPopoverEnabled ? 'true' : undefined"
                :aria-expanded="isPopoverEnabled ? debouncedOpen : undefined"
                :aria-label="ariaLabel"
                tabindex="0"
                class="inline-block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                @click="handleTriggerClick"
                @keydown="handleTriggerKeydown"
            >
                <AnimatePresence mode="wait">
                    <Motion
                        v-if="scrolled || sidebar || oneLocationRouteSlug"
                        key="logo-scrolled"
                        as="div"
                        class="flex items-center"
                        :variants="logoVariants"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        :transition="logoTransition"
                    >
                        <!-- TODO: Determine how to handle overlapping on 200% zoom -->
                        <img
                            :class="cn(
                                'w-[var(--nav-icon-width,1.75rem)] h-auto'
                                /* !isDesktop && !sidebar && 'hidden' */
                            )"
                            :src="concept.images.logo"
                            :alt="`${titleCase(concept.name)} icon`"
                            width="28"
                            height="28"
                        >
                        <img
                            class="w-auto h-[var(--nav-wordmark-height,1.75rem)]"
                            :src="concept.images.wordmark"
                            alt="Restaurant wordmark"
                            width="140"
                            height="28"
                        >
                    </Motion>
                    <Motion
                        v-else
                        key="logo-default"
                        as="img"
                        class="w-[var(--nav-icon-width,1.75rem)] h-auto"
                        :src="concept.images.logo"
                        :alt="`${titleCase(concept.name)} icon`"
                        :variants="logoVariants"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        :transition="logoTransition"
                        width="28"
                        height="28"
                    />
                </AnimatePresence>
            </RouterLink>
        </PopoverTrigger>
        <PopoverContent
            v-if="!hasOneLocation && isDesktop"
            align="start"
            class="w-56 p-2 border-none"
            @mouseenter="open = true"
            @mouseleave="open = false"
            @escapeKeyDown="handleEscapeKeyDown"
        >
            <PopoverArrow class="fill-muted" />
            <ul class="w-full">
                <li
                    v-for="(item, index) of locationItems"
                    :key="item.name"
                    class="flex"
                >
                    <RouterLink
                        :to="item.to"
                        tabindex="0"
                        :aria-label="`Navigate to ${item.name}`"
                        :aria-current="isActive(item.to) ? 'page' : undefined"
                        :class="cn('w-full h-9 px-3 flex items-center transition-colors hover:bg-background/80 hover:text-foreground', isActive(item.to) && 'bg-background text-foreground')"
                        @click="handleSelect(item.to)"
                        @keydown="handleKeydown($event, index)"
                    >
                        <span class="uppercase text-sm font-button capsize">{{ item.name }}</span>
                    </RouterLink>
                </li>
            </ul>
        </PopoverContent>
    </Popover>
</template>
